import { createApp } from "vue";
import App from "@/App.vue";
import router from "@/router";
import vuetify from "./plugins/vuetify";

// @ts-ignore
import { VueDraggableNext } from "vue-draggable-next";
// @ts-ignore
import SimpleTypeahead from "vue3-simple-typeahead";

import "bootstrap";
import "@/assets/base.scss";
import "bootstrap-icons/font/bootstrap-icons.css";

import { createPinia } from "pinia";
import initStorePlugin from "./plugins/initStore";
import { openReplayPlugin } from "./plugins/openreplay";
import { shouldEnableDevtools } from "@/utils/devtools";

// Import Vue i18n and all language files
import { createI18n } from 'vue-i18n'
import en from "@/locales/en.js"; // English
import es from "@/locales/es.js"; // Spanish  
import fr from "@/locales/fr.js"; // French
import de from "@/locales/de.js"; // German
import ar from "@/locales/ar.js"; // Arabic (RTL)
import zh from "@/locales/zh.js"; // Chinese
import ja from "@/locales/ja.js"; // Japanese
import bn from "@/locales/bn.js"; // Bengali
import ko from "@/locales/ko.js"; // Korean
import ur from "@/locales/ur.js"; // Urdu (RTL)
import wo from "@/locales/wo.js"; // Wolof
import ru from "@/locales/ru.js"; // Russian
import uk from "@/locales/uk.js"; // Ukrainian
import zhCn from "@/locales/zh-cn.js"; // Mandarin (Simplified Chinese)
import uz from "@/locales/uz.js"; // Uzbek
import ka from "@/locales/ka.js"; // Georgian
import yue from "@/locales/yue.js"; // Cantonese
import ht from "@/locales/ht.js"; // Haitian Creole
import nl from "@/locales/nl.js"; // Dutch
import vi from "@/locales/vi.js"; // Vietnamese
import it from "@/locales/it.js"; // Italian
import pt from "@/locales/pt.js"; // Portuguese

import dayjs from "dayjs";
import localizedFormat from 'dayjs/plugin/localizedFormat';
// Import dayjs language files (add more as needed)
import dayjs_en from 'dayjs/locale/en'; // English
import dayjs_es from 'dayjs/locale/es'; // Spanish
import dayjs_fr from 'dayjs/locale/fr'; // French
import dayjs_de from 'dayjs/locale/de'; // German
import dayjs_ar from 'dayjs/locale/ar'; // Arabic
import dayjs_zh from 'dayjs/locale/zh'; // Chinese
import dayjs_ja from 'dayjs/locale/ja'; // Japanese
import dayjs_bn from 'dayjs/locale/bn'; // Bengali
import dayjs_ko from 'dayjs/locale/ko'; // Korean
import dayjs_ur from 'dayjs/locale/ur'; // Urdu
import dayjs_ru from 'dayjs/locale/ru'; // Russian
import dayjs_uk from 'dayjs/locale/uk'; // Ukrainian
import dayjs_zh_cn from 'dayjs/locale/zh-cn'; // Mandarin
import dayjs_uz from 'dayjs/locale/uz'; // Uzbek
import dayjs_ka from 'dayjs/locale/ka'; // Georgian
import dayjs_ht from 'dayjs/locale/ht'; // Haitian Creole
import dayjs_nl from 'dayjs/locale/nl'; // Dutch
import dayjs_vi from 'dayjs/locale/vi'; // Vietnamese
import dayjs_it from 'dayjs/locale/it'; // Italian
import dayjs_pt from 'dayjs/locale/pt'; // Portuguese

dayjs.extend(localizedFormat);

// List of RTL languages
const RTL_LANGUAGES = ['ar', 'ur']; // Add more RTL language codes as needed

// Function to check if a language is RTL
function isRTL(locale) {
  return RTL_LANGUAGES.includes(locale);
}

// Function to apply RTL/LTR direction
function applyDirection(locale) {
  const htmlElement = document.documentElement;
  if (isRTL(locale)) {
    htmlElement.setAttribute('dir', 'rtl');
    htmlElement.setAttribute('lang', locale);
    document.body.classList.add('rtl');
  } else {
    htmlElement.setAttribute('dir', 'ltr');
    htmlElement.setAttribute('lang', locale);
    document.body.classList.remove('rtl');
  }
}

// Function to get the locale setting from local storage
function getLocaleFromLocalStorage() {
  return localStorage.getItem('ptcwizard_locale');
}

// Function to set dayjs locale
function setDayjsLocale(locale) {
  const localeMap = {
    'en': 'en',
    'es': 'es', 
    'fr': 'fr',
    'de': 'de',
    'ar': 'ar',
    'zh': 'zh',
    'ja': 'ja',
    'bn': 'bn',
    'ko': 'ko',
    'ur': 'ur',
    'wo': 'en', // Wolof not available in dayjs, fallback to English
    'ru': 'ru',
    'uk': 'uk',
    'zh-cn': 'zh-cn',
    'uz': 'uz',
    'ka': 'ka',
    'yue': 'zh-hk', // Cantonese - use Hong Kong locale
    'ht': 'ht',
    'nl': 'nl',
    'vi': 'vi',
    'it': 'it',
    'pt': 'pt'
  };
  dayjs.locale(localeMap[locale] || 'en');
}

const initialLocale = getLocaleFromLocalStorage() || 'en';

const i18n = createI18n({
  locale: initialLocale,
  fallbackLocale: 'en',
  messages: {
    en,
    es,
    fr,
    de,
    ar,
    zh,
    ja,
    bn,
    ko,
    ur,
    wo,
    ru,
    uk,
    "zh-cn": zhCn,
    uz,
    ka,
    yue,
    ht,
    nl,
    vi,
    it,
    pt
  }, 
})

// Apply initial direction and dayjs locale
applyDirection(initialLocale);
setDayjsLocale(initialLocale);

const pinia = createPinia();
pinia.use(initStorePlugin);

// Attach OpenReplay Pinia plugin so state changes are captured  
// OpenReplay plugin will register the Pinia plugin during its installation
const app = createApp(App);
app.config.devtools = shouldEnableDevtools();
app.config.globalProperties.$log = console.log;
app.config.globalProperties.$i18n = i18n.global;
app.component("Draggable", VueDraggableNext);

app.use(router);
app.use(pinia);
app.use(i18n);
app.use(SimpleTypeahead);
app.use(vuetify);
app.use(openReplayPlugin);

app.mount("#app");

// Export utility functions for use in components
export { isRTL, applyDirection, setDayjsLocale };
